<template>
  <b-container class="mt-5 methods-parameters-table" fluid>
    <b-row align-v="center" class="methods-parameters-table__fixed-header">
      <b-col
        class="text-center methods-parameters-table__header p-3"
      >
        <label class="mb-0">
          {{ $t('general.parameters') }}
        </label>
      </b-col>
      <b-col class="text-center methods-parameters-table__header p-3">
        <b-row
          class="methods-parameters-table__title-text"
          :class="{ 'methods-parameters-table__custom-disabled': selectedMethodHasHiddenOption }"
          align-h="center"
          align-v="center"
        >
          <b-col cols="auto">
            <label class="mb-0">
              {{ $t('general.customization') }}
              <sup>
                <info-badge
                  :string-key="customizationTooltipMessage"
                />
              </sup>
            </label>
          </b-col>
        </b-row>
        <b-row
          v-if="showCustomMenu"
          class="methods-parameters-table__title-icon"
          align-h="end"
          align-v="center"
        >
          <b-col cols="auto" v-if="showDropdownMenu">
            <methods-parameters-dropdown-menu
              :selected-method-has-hidden-option="selectedMethodHasHiddenOption"
              :assetGeneralInfo="assetGeneralInfo"
              :selected-method="selectedMethod"
              :selected-norm="selectedNorm"
              :asset-module="assetModule"
              :is-inside-table="true"
              @handle-visibility="handleVisibility"
              @set-parameters="setParameters"
              @set-parameters-copy="setParametersCopy"
              @call-edit-parameters="callEditParameters"
              @update-loading="handleLoading"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row v-if="!loading" fluid class="methods-parameters-table__body px-0 mt-1">
      <b-col cols="12">
        <b-row
          v-for="({ label, key }, index) in parametersLabels"
          :key="index" class="methods-parameters-table__rows"
          align-v="center"
        >
          <template v-if="showParameterLabel(key)">
            <b-col cols="6" class="methods-parameters-table__border p-3">
              <label class="mb-0">
                {{ label }}
              </label>
            </b-col>
            <b-col
              cols="6"
              class="methods-parameters-table__border text-center p-3"
              :class="{
                      'methods-parameters-table__custom-disabled': selectedMethodHasHiddenOption
                      }">
              <s-input-label-edit
                v-model="parametersCopyProxy[key]"
                :editable="editMode"
                :formatted-value="formatNumberValue(parametersCopyProxy[key], 1, 1)"
              >
                <s-number-input
                  v-model="parametersCopyProxy[key]"
                  :validation="validation.parametersCopy[key]"
                  :numeric-options="inputNumericOptions"
                  :invalid="validation.parametersCopy[key].$invalid"
                />
                <b-form-invalid-feedback id="input-live-feedback">
                  {{ $t('messages.MA086.message', getValueRange(key)) }}
                </b-form-invalid-feedback>
              </s-input-label-edit>
            </b-col>
          </template>
        </b-row>
      </b-col>
      <b-col v-if="!parameters">
        <s-empty-list/>
      </b-col>
    </b-row>
    <b-row class="methods-parameters-table__loader" align-v="center" align-h="center" v-else>
      <s-loader size="lg"/>
    </b-row>
  </b-container>
</template>

<script>
import InfoBadge from '@/components/icon/InfoBadge';
import { Rules, TransformerApplication } from 'sigma-mappings';
import dropdownLimits from '@/domain/data/installation/engineering/chromatography/methodsParameters/dropdownLimits';
import MethodsParametersDropdownMenu
  from '@/domain/components/installation/engineering/chromatography/methodsParameters/DropdownMenu';
import parametersLabels from '@/domain/data/installation/engineering/chromatography/methodsParameters/parametersLabels';

export default {
  name: 'Table',
  components: { InfoBadge, MethodsParametersDropdownMenu },
  props: {
    parametersCopy: {
      type: Object,
    },
    parameters: {
      type: Object,
    },
    selectedMethod: {
      type: String,
    },
    selectedNorm: {
      type: String,
    },
    selectedMethodHasHiddenOption: {
      type: Boolean,
    },
    assetGeneralInfo: {
      type: Object,
    },
    validation: {
      type: Object,
    },
    editMode: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    assetModule: {
      type: Object,
    },
  },
  data: () => ({
    limits: dropdownLimits,
  }),
  computed: {
    showDropdownMenu() {
      const isIecNormAndTransformApplicationPotency = this.selectedNorm === Rules.IEC_60599
        && this.assetGeneralInfo.transformerApplication === TransformerApplication.POTENCY;
      return this.editMode
        && isIecNormAndTransformApplicationPotency;
    },
    inputNumericOptions() {
      return {
        decimalPlaces: 1,
      };
    },
    parametersCopyProxy: {
      get() {
        return this.parametersCopy;
      },
      set(value) {
        this.$emit('update:parameters-copy', value);
      },
    },
    parametersLabels() {
      return parametersLabels(this);
    },
    customizationTooltipMessage() {
      return this.$t('engineering.chromatography.method-parameters.customization-tooltip');
    },
    showCustomMenu() {
      return this.$hasUserPermission('PER013') && !!this.selectedMethod && !!this.selectedNorm;
    },
  },
  methods: {
    getValueRange(key) {
      const validation = this.validation.parametersCopy;
      if (!validation) return null;
      const params = (validation[key] || {}).$params || {};
      return {
        minValue: this.formatNumberValue((params.minValue || {}).min, 1, 1),
        maxValue: this.formatNumberValue((params.maxValue || {}).max, 1, 1),
      };
    },
    handleVisibility(hidden) {
      this.$emit('handle-visibility', hidden);
    },
    formatNumberValue(value, minimumFractionDigits = 0, maximumFractionDigits = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, { minimumFractionDigits, maximumFractionDigits });
    },
    showParameterLabel(key) {
      return this.parameters && this.parameters[key] !== undefined;
    },
    setParameters(values) {
      this.$emit('update:parameters', values);
    },
    setParametersCopy() {
      this.$emit('set-parameters-copy');
    },
    callEditParameters(code) {
      this.$emit('call-edit-parameters', code);
    },
    handleLoading() {
      this.$emit('update-loading');
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"

  @media print
    .methods-parameters-table
      max-height: none !important

  .methods-parameters-table
    overflow-y: auto
    overflow-x: hidden
    max-height: 465px
    &__fixed-header
      display: flex
      flex-direction: row
      flex-wrap: nowrap
      position: sticky
      top: 0
      z-index: 10
    &__header
      min-width: 200px
      background: var(--color-table-body-bg)
      &:nth-child(2n)
        background: var(--color-table-header-bg)
    &__title-text
      height: 1.5em
    &__custom-disabled
      opacity: .5
    &__body
      border: 1px solid var(--color-border-select)
      width: auto
      border-radius: 5px
    &__border
      border-right: 1px solid white
      min-width: 200px
      &:nth-child(2n)
        border-left: 1px solid white
        border-right: 0
    &__title-icon
      margin-top: -1.5em
      height: 1.5em
    &__rows
      display: flex
      flex-direction: row
      flex-wrap: nowrap
      &:nth-child(2n)
        background: var(--color-table-body-accent-bg)
    &__loader
      height: 300px
    ::v-deep .btn
      padding: 0 !important

</style>
